import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink } from "lucide-react";

const EMPTY = { title: "", description: "", sheets_url: "", image_url: "", sort_order: 100, active: true };

export default function AdminPackBreaks() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await api.get("/admin/pack-breaks")).data); }
    catch { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, sort_order: parseInt(editing.sort_order, 10) || 0 };
      await api.post("/admin/pack-breaks", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this break entry?")) return;
    await api.delete(`/admin/pack-breaks/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-4xl">Pack Breaks</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="add-break-btn">
          <Plus size={16} className="inline mr-1 -mt-0.5" /> New Break Entry
        </button>
      </div>
      <p className="text-zinc-400 text-sm mb-8">
        Add entries that appear on the public <span className="text-white">/pack-contents</span> page. Paste a public Google Sheets URL so customers can see the exact contents of each break. Sheets must be shared as "Anyone with the link can view".
      </p>

      {editing && (
        <form onSubmit={save} className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] mb-6 space-y-4" data-testid="break-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required data-testid="break-title" className="input" placeholder="e.g. Feb 15 · NFL National Treasures Case Break" /></Field>
            <Field label="Sort Order"><input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="input" /></Field>
            <Field label="Google Sheets URL" className="md:col-span-2">
              <input type="url" value={editing.sheets_url} onChange={(e) => setEditing({ ...editing, sheets_url: e.target.value })}
                     data-testid="break-sheets" placeholder="https://docs.google.com/spreadsheets/d/…/edit?usp=sharing" className="input" />
            </Field>
            <Field label="Image URL (optional)" className="md:col-span-2">
              <input type="url" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                     placeholder="Optional cover image URL" className="input" />
            </Field>
            <Field label="Description" className="md:col-span-2">
              <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                        data-testid="break-desc" className="input" />
            </Field>
            <Field label="Status">
              <select value={editing.active ? "y" : "n"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "y" })} className="input">
                <option value="y">Visible</option><option value="n">Hidden</option>
              </select>
            </Field>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="break-save">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="breaks-list">
        {items.length === 0 && <div className="text-zinc-500">No break entries yet.</div>}
        {items.map((b) => (
          <div key={b.id} className="flex items-center gap-4 border border-white/10 rounded p-4 bg-[var(--cl-surface)]">
            {b.image_url && <img src={b.image_url} alt="" className="w-16 h-16 object-cover rounded" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium">{b.title}</div>
              {b.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{b.description}</div>}
              {b.sheets_url && (
                <a href={b.sheets_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--cl-orange)] mt-1 inline-flex items-center gap-1">
                  Sheets link <ExternalLink size={10} />
                </a>
              )}
            </div>
            <span className={`text-[10px] px-2 py-1 rounded ${b.active ? "border border-green-500/50 text-green-400" : "border border-white/15 text-zinc-500"}`}>
              {b.active ? "VISIBLE" : "HIDDEN"}
            </span>
            <button onClick={() => setEditing(b)} className="text-xs btn-outline px-3 py-1">Edit</button>
            <button onClick={() => del(b.id)} className="p-2 hover:text-[var(--cl-primary)]" data-testid={`del-break-${b.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-label text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
